import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { X, Minus, Plus, ShoppingCart, BadgeCheck, Maximize2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { getImageUrl } from "../utils/getImageUrl";
import CartToast from "./CartToast";

// Vista rápida de un producto (imagen, descripción completa, precio y agregar al
// carrito) para que el usuario pueda leer la descripción y sumar varios productos al
// carrito sin salir de la vista donde está navegando (grilla de una tienda, ofertas,
// favoritos, etc.) — en vez de forzarlo a entrar a /product/:id por cada uno.
export default function ProductQuickViewModal({ product, onClose, isOpen = true, closedMessage }) {
  const { addToCart, getQty } = useCart();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);

  if (!product) return null;

  const hasOffer = product.offerPrice && Number(product.offerPrice) < Number(product.price);
  const finalPrice = hasOffer ? Number(product.offerPrice) : Number(product.price);
  const outOfStock = product.stock != null && Number(product.stock) <= 0;
  const canAdd = isOpen && !outOfStock;
  const productId = product.id ?? product._id;
  const inCartQty = getQty(product);

  const handleAddToCart = () => {
    if (!canAdd) return;
    for (let i = 0; i < qty; i++) addToCart(product);
    toast(<CartToast product={product} />, { icon: "🛒", autoClose: 2000 });
    onClose();
  };

  return createPortal(
    <div
      // z-[10050]: por encima de MobileBottomBar (z-[9999]) y de su submenú (z-[10000])
      // — antes este modal quedaba con z-[70] y el menú inferior lo tapaba por completo
      // en móvil, obstruyendo justo los botones de cantidad/agregar al carrito.
      className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl overflow-hidden max-h-[85vh] sm:max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Imagen */}
        <div className="relative w-full h-56 bg-gray-100 shrink-0">
          {product.images?.[0] ? (
            <img
              src={getImageUrl(product.images[0])}
              alt={product.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              Sin imagen
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm"
          >
            <X size={16} />
          </button>
          {!isOpen && (
            <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
              {closedMessage || "Cerrado"}
            </div>
          )}
        </div>

        {/* Contenido */}
        <div className="p-5 overflow-y-auto">
          {product.company?.name && (
            <div className="flex items-center gap-1 text-xs text-gray-400 mb-1.5">
              <span>{product.company.name}</span>
              {product.company.verified && <BadgeCheck size={12} className="text-blue-500" />}
            </div>
          )}

          <h2 className="text-lg font-bold text-gray-900 mb-2">{product.title}</h2>

          <div className="flex items-baseline gap-2 mb-3">
            {hasOffer && (
              <span className="text-sm text-gray-400 line-through">
                ${Number(product.price).toLocaleString("es-CO")}
              </span>
            )}
            <span className="text-xl font-black text-supernova-600">
              ${finalPrice.toLocaleString("es-CO")}
            </span>
          </div>

          <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line mb-4">
            {product.description || "Este producto no tiene descripción."}
          </p>

          {productId && (
            <button
              type="button"
              onClick={() => { onClose(); navigate(`/product/${productId}`); }}
              className="flex items-center gap-1.5 text-xs font-semibold text-supernova-600 hover:text-supernova-700 mb-4"
            >
              <Maximize2 size={12} /> Ver página completa del producto
            </button>
          )}
        </div>

        {/* Acciones */}
        <div className="border-t border-gray-100 shrink-0">
          {inCartQty > 0 && (
            <p className="px-4 pt-2 text-xs text-gray-400 text-center">
              Ya tienes {inCartQty} en tu carrito — esto se suma, no lo reemplaza.
            </p>
          )}
          <div
            className="p-4 flex items-center gap-3"
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
          >
            <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-1">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-800"
              >
                <Minus size={14} />
              </button>
              <span className="w-5 text-center text-sm font-bold text-gray-800">{qty}</span>
              <button
                type="button"
                onClick={() => setQty((q) => q + 1)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-800"
              >
                <Plus size={14} />
              </button>
            </div>

            <button
              type="button"
              disabled={!canAdd}
              onClick={handleAddToCart}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition
                ${canAdd
                  ? "bg-supernova-500 hover:bg-supernova-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
            >
              <ShoppingCart size={15} />
              {outOfStock ? "Agotado" : !isOpen ? "Cerrado" : inCartQty > 0 ? `Sumar ${qty} más` : "Agregar al carrito"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
